/**
 * Line Item Drawer Component
 * BuilderBel Construction Project Management System
 *
 * Drawer form for creating/editing estimate line items
 * Fields match backend EstimateLineItem model and Joi validators exactly.
 */

import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { X, DollarSign, Clock, Package, Truck, Wrench } from 'lucide-react';
import type {
  EstimateLineItem,
  CreateLineItemRequest,
  UpdateLineItemRequest,
} from '../../types/estimate';
import SearchableDropdown from '../forms/SearchableDropdown';
import { useConfigDropdown } from '../../hooks/useConfigDropdown';

export interface LineItemDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateLineItemRequest | UpdateLineItemRequest) => void;
  lineItem?: EstimateLineItem | null;
  isLoading?: boolean;
}

interface FormData {
  costCode: string;
  costCodeDescription: string;
  description: string;
  category: string;
  subcategory: string;
  quantity: number;
  unit: string;
  laborUnitCost: number;
  materialUnitCost: number;
  equipmentUnitCost: number;
  subcontractorUnitCost: number;
  otherUnitCost: number;
  laborHours: number;
  crewSize: number;
  productivityRate: number;
  productivityUnit: string;
  notes: string;
}

/** Backend requires UPPERCASE unit values */
const unitOptions: { value: string; label: string }[] = [
  { value: 'EA', label: 'Each (EA)' },
  { value: 'LF', label: 'Linear Feet (LF)' },
  { value: 'SF', label: 'Square Feet (SF)' },
  { value: 'SY', label: 'Square Yards (SY)' },
  { value: 'CF', label: 'Cubic Feet (CF)' },
  { value: 'CY', label: 'Cubic Yards (CY)' },
  { value: 'HR', label: 'Hours (HR)' },
  { value: 'DAY', label: 'Days (DAY)' },
  { value: 'WK', label: 'Weeks (WK)' },
  { value: 'MO', label: 'Months (MO)' },
  { value: 'LS', label: 'Lump Sum (LS)' },
  { value: 'TON', label: 'Tons (TON)' },
  { value: 'LB', label: 'Pounds (LB)' },
  { value: 'GAL', label: 'Gallons (GAL)' },
  { value: 'BAG', label: 'Bags (BAG)' },
];

const LineItemDrawer: React.FC<LineItemDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  lineItem,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      costCode: '',
      costCodeDescription: '',
      description: '',
      category: '',
      subcategory: '',
      quantity: 1,
      unit: 'EA',
      laborUnitCost: 0,
      materialUnitCost: 0,
      equipmentUnitCost: 0,
      subcontractorUnitCost: 0,
      otherUnitCost: 0,
      laborHours: 0,
      crewSize: 0,
      productivityRate: 0,
      productivityUnit: '',
      notes: '',
    },
  });

  // Watch for calculated totals
  const watchQuantity = watch('quantity');
  const watchLaborUnitCost = watch('laborUnitCost');
  const watchMaterialUnitCost = watch('materialUnitCost');
  const watchEquipmentUnitCost = watch('equipmentUnitCost');
  const watchSubcontractorUnitCost = watch('subcontractorUnitCost');
  const watchOtherUnitCost = watch('otherUnitCost');

  // Calculate totals: backend does quantity * unitCost for each
  const qty = watchQuantity || 0;
  const laborTotal = qty * (watchLaborUnitCost || 0);
  const materialTotal = qty * (watchMaterialUnitCost || 0);
  const equipmentTotal = qty * (watchEquipmentUnitCost || 0);
  const subcontractorTotal = qty * (watchSubcontractorUnitCost || 0);
  const otherTotal = qty * (watchOtherUnitCost || 0);
  const lineTotal = laborTotal + materialTotal + equipmentTotal + subcontractorTotal + otherTotal;

  // Configuration-backed category & subcategory dropdowns
  const selectedCategory = watch('category');

  const {
    options: categoryOptions,
    handleCreate: createCategory,
  } = useConfigDropdown('estimate-categories');

  const {
    options: subcategoryOptions,
    handleCreate: createSubcategory,
  } = useConfigDropdown('estimate-subcategories', {
    filterFn: opt => opt.metadata?.category === selectedCategory,
  });

  const handleCategoryChange = useCallback(
    (value: string) => {
      setValue('category', value, { shouldValidate: true });
      setValue('subcategory', '');
    },
    [setValue]
  );

  const handleSubcategoryChange = useCallback(
    (value: string) => {
      setValue('subcategory', value);
    },
    [setValue]
  );

  const handleCreateCategory = useCallback(
    async (name: string) => {
      const value = await createCategory(name);
      if (value) {
        setValue('category', value, { shouldValidate: true });
        setValue('subcategory', '');
      }
    },
    [createCategory, setValue]
  );

  const handleCreateSubcategory = useCallback(
    async (name: string) => {
      const value = await createSubcategory(name, { category: selectedCategory });
      if (value) {
        setValue('subcategory', value);
      }
    },
    [createSubcategory, selectedCategory, setValue]
  );

  useEffect(() => {
    if (lineItem) {
      reset({
        costCode: lineItem.costCode || '',
        costCodeDescription: lineItem.costCodeDescription || '',
        description: lineItem.description,
        category: lineItem.category,
        subcategory: lineItem.subcategory || '',
        quantity: lineItem.quantity,
        unit: lineItem.unit,
        laborUnitCost: lineItem.laborUnitCost || 0,
        materialUnitCost: lineItem.materialUnitCost || 0,
        equipmentUnitCost: lineItem.equipmentUnitCost || 0,
        subcontractorUnitCost: lineItem.subcontractorUnitCost || 0,
        otherUnitCost: lineItem.otherUnitCost || 0,
        laborHours: lineItem.laborHours || 0,
        crewSize: lineItem.crewSize || 0,
        productivityRate: lineItem.productivityRate || 0,
        productivityUnit: lineItem.productivityUnit || '',
        notes: lineItem.notes || '',
      });
    } else {
      reset({
        costCode: '',
        costCodeDescription: '',
        description: '',
        category: '',
        subcategory: '',
        quantity: 1,
        unit: 'EA',
        laborUnitCost: 0,
        materialUnitCost: 0,
        equipmentUnitCost: 0,
        subcontractorUnitCost: 0,
        otherUnitCost: 0,
        laborHours: 0,
        crewSize: 0,
        productivityRate: 0,
        productivityUnit: '',
        notes: '',
      });
    }
  }, [lineItem, reset]);

  const handleFormSubmit = (data: FormData) => {
    const base = {
      costCode: data.costCode,
      costCodeDescription: data.costCodeDescription,
      description: data.description,
      category: data.category,
      subcategory: data.subcategory || undefined,
      quantity: data.quantity,
      unit: data.unit.toUpperCase(),
      laborUnitCost: data.laborUnitCost || undefined,
      materialUnitCost: data.materialUnitCost || undefined,
      equipmentUnitCost: data.equipmentUnitCost || undefined,
      subcontractorUnitCost: data.subcontractorUnitCost || undefined,
      otherUnitCost: data.otherUnitCost || undefined,
      laborHours: data.laborHours || undefined,
      crewSize: data.crewSize || undefined,
      productivityRate: data.productivityRate || undefined,
      productivityUnit: data.productivityUnit || undefined,
      notes: data.notes || undefined,
    };

    if (lineItem) {
      // Update — costCode is not updatable per backend validator
      const { costCode, ...updateFields } = base;
      onSubmit(updateFields as UpdateLineItemRequest);
    } else {
      onSubmit(base as CreateLineItemRequest);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed inset-6 z-[100] flex justify-end pointer-events-none">
        <div className="w-[90%] max-w-4xl pointer-events-auto animate-in slide-in-from-right duration-300">
          <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {lineItem ? 'Edit Line Item' : 'Add Line Item'}
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(handleFormSubmit)} className="flex-1 overflow-y-auto">
              <div className="px-8 py-6 space-y-6">
                {/* Basic Info Section */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Cost Code *
                      </label>
                      <input
                        type="text"
                        {...register('costCode', { required: 'Cost code is required' })}
                        placeholder="e.g., 03-1000"
                        disabled={!!lineItem}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-100 disabled:text-gray-500"
                      />
                      {errors.costCode && (
                        <p className="text-xs text-red-500 mt-1">{errors.costCode.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Cost Code Description *
                      </label>
                      <input
                        type="text"
                        {...register('costCodeDescription', {
                          required: 'Cost code description is required',
                        })}
                        placeholder="e.g., Cast-in-Place Concrete"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                      {errors.costCodeDescription && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.costCodeDescription.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <SearchableDropdown
                        label="Category *"
                        placeholder="Select category"
                        options={categoryOptions}
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        error={errors.category?.message}
                        creatable
                        onCreateNew={handleCreateCategory}
                        searchPlaceholder="Search or create category..."
                      />
                      <input type="hidden" {...register('category', { required: 'Category is required' })} />
                    </div>
                    <div>
                      <SearchableDropdown
                        label="Subcategory"
                        placeholder={selectedCategory ? 'Select subcategory' : 'Select a category first'}
                        options={subcategoryOptions}
                        value={watch('subcategory')}
                        onChange={handleSubcategoryChange}
                        disabled={!selectedCategory}
                        creatable={!!selectedCategory}
                        onCreateNew={handleCreateSubcategory}
                        searchPlaceholder="Search or create subcategory..."
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Description *
                      </label>
                      <input
                        type="text"
                        {...register('description', { required: 'Description is required' })}
                        placeholder="Enter line item description"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                      {errors.description && (
                        <p className="text-xs text-red-500 mt-1">{errors.description.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quantity & Unit */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Package className="h-4 w-4 text-gray-500" />
                    Quantity & Unit
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Quantity *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        {...register('quantity', {
                          required: 'Quantity is required',
                          valueAsNumber: true,
                          min: { value: 0.01, message: 'Must be greater than 0' },
                        })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                      {errors.quantity && (
                        <p className="text-xs text-red-500 mt-1">{errors.quantity.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Unit *</label>
                      <select
                        {...register('unit', { required: 'Unit is required' })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      >
                        {unitOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Unit Costs — each is cost per unit, total = quantity × unitCost */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-blue-500" />
                    Unit Costs (per unit)
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    {/* Labor */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        <Clock className="h-3 w-3 inline mr-1 text-blue-500" />
                        Labor ($/unit)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                          $
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          {...register('laborUnitCost', { valueAsNumber: true })}
                          className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>
                      <p className="text-xs text-blue-600 mt-1">= {formatCurrency(laborTotal)}</p>
                    </div>

                    {/* Material */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        <Truck className="h-3 w-3 inline mr-1 text-green-500" />
                        Material ($/unit)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                          $
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          {...register('materialUnitCost', { valueAsNumber: true })}
                          className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>
                      <p className="text-xs text-green-600 mt-1">
                        = {formatCurrency(materialTotal)}
                      </p>
                    </div>

                    {/* Equipment */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        <Wrench className="h-3 w-3 inline mr-1 text-yellow-500" />
                        Equipment ($/unit)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                          $
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          {...register('equipmentUnitCost', { valueAsNumber: true })}
                          className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>
                      <p className="text-xs text-yellow-600 mt-1">
                        = {formatCurrency(equipmentTotal)}
                      </p>
                    </div>

                    {/* Subcontractor */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Subcontractor ($/unit)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                          $
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          {...register('subcontractorUnitCost', { valueAsNumber: true })}
                          className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>
                      <p className="text-xs text-purple-600 mt-1">
                        = {formatCurrency(subcontractorTotal)}
                      </p>
                    </div>

                    {/* Other */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Other ($/unit)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                          $
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          {...register('otherUnitCost', { valueAsNumber: true })}
                          className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">= {formatCurrency(otherTotal)}</p>
                    </div>
                  </div>
                </div>

                {/* Productivity (optional) */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    Productivity (Optional)
                  </h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Labor Hours
                      </label>
                      <input
                        type="number"
                        step="0.5"
                        {...register('laborHours', { valueAsNumber: true })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Crew Size
                      </label>
                      <input
                        type="number"
                        step="1"
                        {...register('crewSize', { valueAsNumber: true })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Prod. Rate
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        {...register('productivityRate', { valueAsNumber: true })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Prod. Unit
                      </label>
                      <input
                        type="text"
                        {...register('productivityUnit')}
                        placeholder="e.g., SF/HR"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Notes</h3>
                  <textarea
                    {...register('notes')}
                    rows={3}
                    placeholder="Additional notes..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
                  />
                </div>
              </div>

              {/* Footer with Total */}
              <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-gray-600 space-y-1">
                    <div className="flex gap-4">
                      <span>
                        Labor:{' '}
                        <span className="font-medium text-blue-700">
                          {formatCurrency(laborTotal)}
                        </span>
                      </span>
                      <span>
                        Material:{' '}
                        <span className="font-medium text-green-700">
                          {formatCurrency(materialTotal)}
                        </span>
                      </span>
                      <span>
                        Equipment:{' '}
                        <span className="font-medium text-yellow-700">
                          {formatCurrency(equipmentTotal)}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-gray-500 block">Line Total</span>
                    <span className="text-2xl font-bold text-gray-900">
                      {formatCurrency(lineTotal)}
                    </span>
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-6 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Saving...' : lineItem ? 'Update' : 'Add Line Item'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default LineItemDrawer;
